import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/ui/footer";
import { Navbar } from "@/components/ui/navbar";
import { ArrowRight, Check, Send } from "lucide-react";
import { submitEventSuggestion } from "@/server/suggestEvent";

export const Route = createFileRoute("/suggest")({
  component: SuggestEventPage,
});

function SuggestEventPage() {
  const [eventUrl, setEventUrl] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await submitEventSuggestion({
        data: {
          eventUrl,
          email: email || undefined,
          notes: notes || undefined,
        },
      });

      if (result.success) {
        setIsSubmitted(true);
      } else {
        alert(result.error);
      }
    } catch (error) {
      console.error("Error submitting suggestion:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-6 py-20">
          <div className="max-w-md text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
              Thanks for the suggestion!
            </h1>
            <p className="text-slate-600 mb-8">
              We'll review the event and add it to Wayra if it's a good fit.
              We'll let you know once it's live.
            </p>
            <Button
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-6"
              onClick={() => {
                setIsSubmitted(false);
                setEventUrl("");
                setEmail("");
                setNotes("");
              }}
            >
              Suggest Another Event
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-50 via-orange-50/30 to-slate-50 py-16 lg:py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-2xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-1.5 text-sm font-medium mb-6">
                <Send className="w-4 h-4" />
                Community Powered
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
                Suggest an Event
              </h1>
              <p className="text-lg text-slate-600">
                Know of a gravel race, ultra, or endurance event that should be
                on Wayra? Send us the link and we'll add it to our index.
              </p>
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-xl mx-auto">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Event URL */}
                <div>
                  <label
                    htmlFor="eventUrl"
                    className="block text-sm font-medium text-slate-900 mb-2"
                  >
                    Event URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    id="eventUrl"
                    required
                    placeholder="https://example.com/gravel-race"
                    value={eventUrl}
                    onChange={(e) => setEventUrl(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                  />
                  <p className="mt-2 text-sm text-slate-500">
                    Link to the event page on BikeReg, Active, the event's
                    website, or anywhere else you found it
                  </p>
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-slate-900 mb-2"
                  >
                    Your Email{" "}
                    <span className="text-slate-400">(optional)</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                  />
                  <p className="mt-2 text-sm text-slate-500">
                    We'll notify you when the event is added
                  </p>
                </div>

                {/* Notes */}
                <div>
                  <label
                    htmlFor="notes"
                    className="block text-sm font-medium text-slate-900 mb-2"
                  >
                    Notes <span className="text-slate-400">(optional)</span>
                  </label>
                  <textarea
                    id="notes"
                    rows={4}
                    placeholder="Anything else we should know about this event?"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 text-white rounded-full py-6 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    "Submitting..."
                  ) : (
                    <>
                      Submit Suggestion
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              {/* Additional Info */}
              <div className="mt-12 p-6 bg-slate-50 rounded-2xl">
                <h3 className="font-semibold text-slate-900 mb-3">
                  What events are we looking for?
                </h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    <span>Gravel races and gravel grinders</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    <span>Ultra-endurance cycling events</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    <span>Bikepacking races and events</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    <span>Randonneuring brevets and permanents</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    <span>Mixed-surface and adventure rides</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
